import { useRender } from '@base-ui/react/use-render';
import { createLogger } from '@engram/logger';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { cn } from '../../lib/cn';

// FocusArea — a role-neutral roving-tabindex focus system, built from
// FocusArea (coordinator, routes directional exits between zones), FocusGroup
// (a 1D roving list), and FocusItem (a single focusable cell). Focus mechanics
// only; callers attach role/aria-* for meaning.

const logger = createLogger('ui:FocusArea');

type FocusDirection = 'up' | 'down' | 'left' | 'right';

/**
 * Where focus lands when a zone is entered from a neighbour: an end (`'first'` /
 * `'last'`), the zone's last-focused item (`'remembered'`), or a specific index.
 */
export type FocusEntryPoint = 'first' | 'last' | 'remembered' | number;

/**
 * Result of {@link FocusAreaProps.resolveNeighbor}:
 * - `{ to, at }` → the coordinator moves focus to that zone;
 * - `'handled'` → the resolver did the work itself (e.g. closed a popover);
 * - `null`/`undefined` → not handled; the key bubbles so a parent can react.
 */
export type ResolveNeighborResult =
  | { to: string; at?: FocusEntryPoint }
  | 'handled'
  | null
  | undefined;

interface ZoneHandle {
  /** Returns true when an element was actually focused; false when the zone is
   * empty or its element is gone (so the caller can let the key bubble). */
  focus: (at: FocusEntryPoint) => boolean;
}

interface FocusAreaContextValue {
  registerZone: (id: string, handle: ZoneHandle) => () => void;
  /** Returns true when the exit was handled (focus moved or resolver said so). */
  requestExit: (fromId: string, direction: FocusDirection, fromIndex: number) => boolean;
}

const FocusAreaContext = createContext<FocusAreaContextValue | null>(null);

export interface FocusAreaProps {
  /**
   * Resolves where a directional exit from a zone should go. Called when a
   * FocusGroup receives a cross-axis key, or an arrow that would move past a
   * clamped end. `fromIndex` is the active item index in the originating group
   * (use it to pick a target that depends on which item you left from).
   */
  resolveNeighbor?: (
    fromId: string,
    direction: FocusDirection,
    fromIndex: number,
  ) => ResolveNeighborResult;
  children: ReactNode;
}

/** Coordinator. Renders no DOM — it only provides routing context. */
export function FocusArea({ resolveNeighbor, children }: FocusAreaProps) {
  // Keeps the context value stable while callers pass a fresh inline
  // `resolveNeighbor` each render.
  const resolveRef = useRef(resolveNeighbor);
  useEffect(() => {
    resolveRef.current = resolveNeighbor;
  });

  const value = useMemo<FocusAreaContextValue>(() => {
    const zones = new Map<string, ZoneHandle>();
    return {
      registerZone: (id, handle) => {
        if (zones.has(id)) {
          logger.warn('Duplicate FocusArea zone id; the later registration wins', { id });
        }
        zones.set(id, handle);
        return () => {
          // Only drop the entry if it's still ours (guards a remount race that
          // registers the new handle before the old one unregisters).
          if (zones.get(id) === handle) {
            zones.delete(id);
          }
        };
      },
      requestExit: (fromId, direction, fromIndex) => {
        const result = resolveRef.current?.(fromId, direction, fromIndex);
        if (!result) {
          return false;
        }
        if (result === 'handled') {
          return true;
        }
        const handle = zones.get(result.to);
        if (!handle) {
          logger.warn('resolveNeighbor pointed at an unregistered zone', {
            from: fromId,
            to: result.to,
          });
          return false;
        }
        // Only claim the exit if focus actually landed — an empty or unmounted
        // target zone no-ops, and reporting success would swallow the key.
        return handle.focus(result.at ?? 'remembered');
      },
    };
  }, []);

  return <FocusAreaContext.Provider value={value}>{children}</FocusAreaContext.Provider>;
}

interface FocusGroupContextValue {
  /** Active item index, clamped to the live item count. */
  activeIndex: number;
  registerItem: (index: number, node: HTMLElement) => () => void;
  onItemFocus: (index: number) => void;
  onItemKeyDown: (index: number, event: React.KeyboardEvent) => void;
}

const FocusGroupContext = createContext<FocusGroupContextValue | null>(null);

export interface FocusGroupProps {
  /** Arrow axis: vertical = ↑↓, horizontal = ←→. */
  orientation: 'horizontal' | 'vertical';
  /** Zone id — required to participate in a FocusArea handoff. */
  id?: string;
  /** Controlled active item index. */
  activeIndex?: number;
  /** Initial active index when uncontrolled. @default 0 */
  defaultActiveIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  children: ReactNode;
}

/** Pure context provider — renders no DOM; wrap its children with your own element. */
export function FocusGroup({
  orientation,
  id,
  activeIndex,
  defaultActiveIndex = 0,
  onActiveIndexChange,
  children,
}: FocusGroupProps) {
  const area = useContext(FocusAreaContext);

  const [uncontrolled, setUncontrolled] = useState(defaultActiveIndex);
  const isControlled = activeIndex !== undefined;
  const active = isControlled ? activeIndex : uncontrolled;

  // The last index focused here, so a neighbour can re-enter where the user
  // left off (`at: 'remembered'`).
  const stateRef = useRef({ isControlled, onActiveIndexChange });
  const rememberedRef = useRef(active);
  useEffect(() => {
    stateRef.current = { isControlled, onActiveIndexChange };
    rememberedRef.current = active;
  });

  // Items belong by context, so the DOM can't be queried by a shared ancestor.
  // `itemCount` mirrors the registry's size as state for the tab-stop clamp.
  const itemsRef = useRef(new Map<number, HTMLElement>());
  const [itemCount, setItemCount] = useState(0);

  // Stable behaviour API — independent of `active` (it reads refs/registry), so
  // item-registration effects and the zone handle keep a steady identity.
  const api = useMemo(() => {
    const bump = () => {
      setItemCount(itemsRef.current.size);
    };
    const count = () => itemsRef.current.size;
    // Returns whether a registered element was actually focused, so callers can
    // avoid moving the active tab stop (and firing onActiveIndexChange) onto an
    // index whose node is missing — that would desync aria-selected from focus.
    const focusIndex = (index: number): boolean => {
      const node = itemsRef.current.get(index);
      if (!node) {
        return false;
      }
      node.focus();
      return true;
    };
    const setActive = (index: number) => {
      rememberedRef.current = index;
      if (!stateRef.current.isControlled) {
        setUncontrolled(index);
      }
      stateRef.current.onActiveIndexChange?.(index);
    };

    const registerItem = (index: number, node: HTMLElement) => {
      itemsRef.current.set(index, node);
      bump();
      return () => {
        if (itemsRef.current.get(index) === node) {
          itemsRef.current.delete(index);
          bump();
        }
      };
    };

    const focusEntry = (at: FocusEntryPoint): boolean => {
      const n = count();
      if (n === 0) {
        return false;
      }
      const requested =
        at === 'first'
          ? 0
          : at === 'last'
            ? n - 1
            : at === 'remembered'
              ? rememberedRef.current
              : at;
      const index = Math.max(0, Math.min(requested, n - 1));
      if (!focusIndex(index)) {
        return false;
      }
      setActive(index);
      return true;
    };

    const onItemKeyDown = (index: number, event: React.KeyboardEvent) => {
      const vertical = orientation === 'vertical';
      const mainNext = vertical ? 'ArrowDown' : 'ArrowRight';
      const mainPrev = vertical ? 'ArrowUp' : 'ArrowLeft';

      let direction: FocusDirection | null = null;
      let delta = 0;
      if (event.key === mainNext) {
        delta = 1;
        direction = vertical ? 'down' : 'right';
      } else if (event.key === mainPrev) {
        delta = -1;
        direction = vertical ? 'up' : 'left';
      } else if (event.key === 'ArrowDown') {
        direction = 'down';
      } else if (event.key === 'ArrowUp') {
        direction = 'up';
      } else if (event.key === 'ArrowRight') {
        direction = 'right';
      } else if (event.key === 'ArrowLeft') {
        direction = 'left';
      } else {
        return; // Enter/Space/etc. — left to the item / native element.
      }

      if (delta !== 0) {
        const target = index + delta;
        if (target >= 0 && target < count() && focusIndex(target)) {
          event.preventDefault();
          setActive(target);
          return;
        }
        // Past a clamped end (or a missing node) — fall through to the
        // cross-zone handoff so the key isn't silently swallowed.
      }

      const handled =
        direction !== null && id !== undefined
          ? (area?.requestExit(id, direction, index) ?? false)
          : false;
      if (handled) {
        event.preventDefault();
      }
    };

    return { setActive, registerItem, focusEntry, onItemKeyDown };
  }, [orientation, area, id]);

  // Clamp the active tab stop against the live item count so a group always
  // keeps exactly one Tab-reachable item, even if the active one unmounts.
  const activeForTab = itemCount > 0 ? Math.min(active, itemCount - 1) : active;

  useFocusAreaZone(area, id, api.focusEntry);

  const groupValue = useMemo<FocusGroupContextValue>(
    () => ({
      activeIndex: activeForTab,
      registerItem: api.registerItem,
      onItemFocus: api.setActive,
      onItemKeyDown: api.onItemKeyDown,
    }),
    [activeForTab, api],
  );

  return <FocusGroupContext.Provider value={groupValue}>{children}</FocusGroupContext.Provider>;
}

const focusItemVariants = cva('', {
  variants: {
    /**
     * Row treatment. `none` adds no classes, so the caller's element keeps its own look.
     * `drawer-header` is the full-width accordion header of a card drawer. `drawer-row` is an
     * indented row inside a drawer; it darkens while its popover is open. `drawer-row-muted`
     * is a quiet indented row, such as "show more". `option` is a listbox option that shows
     * `selected`.
     */
    variant: {
      none: '',
      'drawer-header':
        'text-text-primary flex w-full cursor-pointer items-center gap-3 p-4 text-left text-base font-semibold hover:bg-neutral-50 focus:outline-none focus-visible:bg-neutral-100 lg:py-3',
      'drawer-row':
        'data-popup-open:bg-neutral-150 flex w-full cursor-pointer items-center gap-2 py-2 pr-4 pl-11 text-left text-base hover:bg-neutral-50 focus:outline-none focus-visible:bg-neutral-100',
      'drawer-row-muted':
        'text-text-tertiary hover:text-text-primary flex w-full cursor-pointer py-2 pr-4 pl-11 text-left text-sm hover:bg-neutral-50 focus:outline-none focus-visible:bg-neutral-100',
      option:
        'aria-selected:bg-neutral-150 flex cursor-default flex-row items-baseline justify-between gap-3 rounded-md px-2 py-1.5 text-sm not-aria-selected:hover:bg-neutral-100 focus:outline-none',
    },
    /** `critical` marks a `drawer-row` with the critical left rule and background. */
    tone: {
      neutral: '',
      critical: '',
    },
  },
  compoundVariants: [
    {
      variant: 'drawer-row',
      tone: 'critical',
      className:
        'bg-critical-50 border-critical-700 data-popup-open:bg-critical-100 border-l-2 pl-[2.4rem]',
    },
  ],
  defaultVariants: {
    variant: 'none',
    tone: 'neutral',
  },
});

export interface FocusItemProps
  extends
    Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'onKeyDown' | 'onFocus'>,
    VariantProps<typeof focusItemVariants> {
  /** Position within the group (0-based, contiguous). */
  index: number;
  /** Keyboard activation (Enter/Space). Click is left to the rendered element. */
  onPressed?: () => void;
  /** Sets aria-selected + data-selected for the listbox/option pattern. */
  selected?: boolean;
  /** Replace/compose the element (e.g. a PopoverTrigger). @default <div> */
  render?: useRender.RenderProp;
  className?: string;
  children?: ReactNode;
  onClick?: React.MouseEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
  onFocus?: React.FocusEventHandler;
}

export function FocusItem({
  index,
  onPressed,
  selected,
  render,
  variant,
  tone,
  className,
  onClick,
  onKeyDown,
  onFocus,
  ...rest
}: FocusItemProps) {
  const group = useContext(FocusGroupContext);
  const nodeRef = useRef<HTMLElement>(null);
  useFocusGroupItemRegistration(group?.registerItem, index, nodeRef);

  // Outside a FocusGroup the item degrades to a plain, Tab-reachable cell
  // (no roving) — so a row can render standalone, e.g. in unit tests.
  const isActive = group ? group.activeIndex === index : true;

  const props = {
    ...rest,
    className: cn(focusItemVariants({ variant, tone }), className) || undefined,
    'data-focus-item': index,
    tabIndex: isActive ? 0 : -1,
    'aria-selected': selected,
    'data-selected': selected,
    onFocus: (event: React.FocusEvent) => {
      group?.onItemFocus(index);
      onFocus?.(event);
    },
    onClick: (event: React.MouseEvent) => {
      // Click marks the item active (selection-follows-focus), independent of
      // whether the browser focuses it. onPressed stays keyboard-only.
      group?.onItemFocus(index);
      onClick?.(event);
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      group?.onItemKeyDown(index, event);
      // Guard on tag: Enter on a native <button> also synthesises a click.
      const isSpace = event.key === ' ';
      const isEnterOnNonButton = event.key === 'Enter' && event.currentTarget.tagName !== 'BUTTON';
      if (!event.defaultPrevented && onPressed && (isSpace || isEnterOnNonButton)) {
        event.preventDefault();
        onPressed();
      }
      onKeyDown?.(event);
    },
  };

  return useRender({
    render: render ?? <div />,
    ref: nodeRef,
    props,
    defaultTagName: 'div',
  });
}

export interface UseFocusZoneResult {
  /** Ask the FocusArea to move focus out of this zone; true when handled. */
  requestExit: (direction: FocusDirection) => boolean;
}

/**
 * Registers `ref`'s element as a FocusArea zone so neighbours can hand focus to
 * it (e.g. a scrollable preview region beside a list). Use {@link FocusGroup}
 * for multi-item lists.
 */
export function useFocusZone({
  id,
  ref,
}: {
  id: string;
  ref: RefObject<HTMLElement | null>;
}): UseFocusZoneResult {
  const area = useContext(FocusAreaContext);
  const focus = useCallback(
    (_at: FocusEntryPoint): boolean => {
      const element = ref.current;
      if (!element) {
        return false;
      }
      element.focus();
      return true;
    },
    [ref],
  );
  useFocusAreaZone(area, id, focus);

  return {
    requestExit: (direction) => area?.requestExit(id, direction, 0) ?? false,
  };
}

/* oxlint-disable engram/no-use-effect -- Sanctioned: syncing with a parent focus registry */
function useFocusAreaZone(
  area: FocusAreaContextValue | null,
  id: string | undefined,
  focus: (at: FocusEntryPoint) => boolean,
) {
  useEffect(() => {
    if (!area || !id) {
      return noop;
    }
    return area.registerZone(id, { focus });
  }, [area, id, focus]);
}

function useFocusGroupItemRegistration(
  register: ((index: number, node: HTMLElement) => () => void) | undefined,
  index: number,
  nodeRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!register) {
      return noop;
    }
    const node = nodeRef.current;
    if (!node) {
      // Usually a `render` element that drops its ref, leaving no registry entry.
      logger.warn('FocusItem ref not attached; item is unreachable by keyboard', { index });
      return noop;
    }
    return register(index, node);
  }, [register, index, nodeRef]);
}

function noop() {}
/* oxlint-enable engram/no-use-effect */
